<script lang="ts">
	/**
	 * One labelled row of a form: label line, the control itself, then helper or
	 * error text underneath.
	 *
	 * Every settings screen in this design language repeats the same three-part
	 * shape — a `<Label>`, the input, and a `text-xs text-gray-500 mt-1` note.
	 * Hand-writing it drifts almost immediately (the note ends up `text-sm` here,
	 * the label loses its bottom margin there), so the rhythm lives here instead
	 * and callers only supply the control.
	 *
	 * Long explanations go in `helpText` rather than under the field: they hide
	 * behind a `?` next to the label, so a dense form stays scannable and the
	 * detail is still one hover away. Short, always-relevant notes ("Enable for
	 * port 465") belong in `description`, which is always visible.
	 *
	 * Grouping convention, so fields breathe consistently:
	 *   <form class="space-y-4"> … </form>            — stacked fields
	 *   <div class="grid sm:grid-cols-2 gap-4"> … </div> — side-by-side fields
	 * Side-by-side means `sm:grid-cols-2`, never a bare `grid-cols-2`: most of these forms live in
	 * a modal, which is as wide as the phone it is on minus its padding, and two columns of about
	 * 160px wrap every label and every value in them.
	 * Wrap a related group in a `Panel` (or a plain `widget`) to give it a title.
	 *
	 * Slots: default (the control), `labelRight` (button/badge pushed to the far
	 * right of the label line), `error` (rich error content, overrides `error`).
	 */

	import { Label, Popover } from 'flowbite-svelte';

	/** Label text. Omit to render the control with no label line at all. */
	export let label: string = '';
	/** Appends the ` *` marker the rest of the app uses for mandatory fields. */
	export let required: boolean = false;
	/** `for`/`id` wiring — pass the same value as the control's `id`. */
	export let id: string = '';

	/** Always-visible note under the control. Hidden while an error is shown. */
	export let description: string = '';
	/** Longer explanation, revealed by the `?` beside the label. */
	export let helpText: string = '';
	/** Documentation link. Takes precedence over `helpText` — one `?` only. */
	export let helpURL: string = '';

	/**
	 * Validation message. Non-empty swaps the helper note for red error text and
	 * marks the row `aria-invalid`, so a field can go from valid to invalid
	 * without the layout shifting.
	 */
	export let error: string = '';

	let className: string = '';
	export { className as class };

	$: hasErrorSlot = $$slots.error;
	$: hasError = hasErrorSlot || error.length > 0;
</script>

<div class={className} aria-invalid={hasError ? 'true' : undefined}>
	{#if label.length > 0 || $$slots.labelRight}
		<div class="flex gap-2 items-center mb-2">
			{#if label.length > 0}
				<Label for={id}>{label}{required ? ' *' : ''}</Label>
			{/if}

			{#if helpURL.length > 0}
				<a href={helpURL} target="_blank" rel="noopener" class="text-blue-600 dark:text-blue-400 hover:text-primary-500">
					<i class="fa-solid fa-circle-question" />
				</a>
			{:else if helpText.length > 0}
				<!-- The Popover attaches to its previous sibling, so this pair must stay adjacent. -->
				<div class="cursor-help text-blue-600 dark:text-blue-400 hover:text-primary-500">
					<i class="fa-solid fa-circle-question" />
				</div>
				<Popover color="dropdown" class="w-64 shadow-2xl shadow-blue-800 text-base font-normal z-30" placement="top">{helpText}</Popover>
			{/if}

			{#if $$slots.labelRight}
				<span class="grow" />
				<slot name="labelRight" />
			{/if}
		</div>
	{/if}

	<slot />

	{#if hasError}
		<p class="text-xs text-red-600 dark:text-red-500 mt-1">
			{#if hasErrorSlot}
				<slot name="error" />
			{:else}
				{error}
			{/if}
		</p>
	{:else if description.length > 0}
		<p class="text-xs text-gray-500 mt-1">{description}</p>
	{/if}
</div>
